<script setup>
import KakaoLoginButton from '@/components/KakaoLoginComponent.vue';
import NaverLoginButton from '@/components/NaverLoginComponent.vue';
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useAuthStore } from '@/store/auth-store';
import { useLoadingStore } from '@/store/loading-store';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

// --- Props ---
const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    initialEmailId: {
        type: String,
        default: ''
    }
});

// --- Emits ---
const emit = defineEmits(['update:modelValue', 'login-success', 'login-fail', 'request-signup', 'request-password-reset', 'social-needs-signup']);

const router = useRouter();
const authStore = useAuthStore();
const loadingStore = useLoadingStore();
const loginid = ref('');
const password = ref('');
const rememberMeCustomer = ref(false);
const showPassword = ref(false);
const toastComponentRef = ref(null);
const isCapsLockOn = ref(false);
const isVisible = ref(props.modelValue);
const accessToken = ref('');
const platform = ref(null);
const showModal = ref(false);
const resetIsEmail = ref(true);
const resetName = ref('');
const resetLoginId = ref('');
const resetPhone = ref('');

// --- Watchers ---
watch(
    () => props.modelValue,
    (newValue) => {
        isVisible.value = newValue;
        if (newValue) {
            password.value = '';
            const savedLoginId = localStorage.getItem('rememberMeCustomer');
            if (savedLoginId) {
                loginid.value = savedLoginId;
                rememberMeCustomer.value = true;
            } else if (props.initialEmailId) {
                loginid.value = props.initialEmailId;
                rememberMeCustomer.value = false; // Don't assume remember me if prefilled by prop
            } else {
                loginid.value = ''; // Clear ID if not remembered or prefilled
                rememberMeCustomer.value = false;
            }
            addCapsLockListeners();
        } else {
            removeCapsLockListeners();
        }
    }
);

watch(isVisible, (newValue) => {
    if (newValue !== props.modelValue) {
        emit('update:modelValue', newValue);
    }
});

const checkCapsLock = (event) => {
    if (typeof event.getModifierState === 'function') {
        isCapsLockOn.value = event.getModifierState('CapsLock');
    }
};

const addCapsLockListeners = () => {
    window.addEventListener('keydown', checkCapsLock);
    window.addEventListener('keyup', checkCapsLock);
};

const removeCapsLockListeners = () => {
    window.removeEventListener('keydown', checkCapsLock);
    window.removeEventListener('keyup', checkCapsLock);
    isCapsLockOn.value = false; // Reset state when not listening
};

const handleLogin = async () => {
    try {
        loadingStore.showLoading();

        const response = await authStore.login({
            email_id: loginid.value,
            password: password.value
        });

        if (rememberMeCustomer.value) {
            localStorage.setItem('rememberMeCustomer', loginid.value);
        } else {
            localStorage.removeItem('rememberMeCustomer');
        }

        emit('login-success');
        closeModal();
    } catch (error) {
        const errorMsg = error.response?.data?.msg || error.message || '아이디 또는 패스워드를 확인하세요';
        console.error(errorMsg);
        if (toastComponentRef.value) {
            toastComponentRef.value.showToast('info', '로그인 실패', errorMsg);
        }
        emit('login-fail', { message: errorMsg });
    } finally {
        loadingStore.hideLoading();
    }
};

const handleSocialLogin = async (access_token, refresh_token, email, name, mobile, birth_date) => {
    try {
        loadingStore.showLoading();
        const response = await authStore.sociallogin({
            access_token: access_token,
            refresh_token: refresh_token,
            email: email,
            name: name,
            mobile: mobile,
            birth_date: birth_date
        });

        emit('login-success');
        closeModal();
    } catch (error) {
        const errorMsg = error.response?.data?.msg || error.message || '소셜 정보 확인 중 오류 발생';
        console.error(errorMsg);
        if (toastComponentRef.value) {
            toastComponentRef.value.showToast('info', '로그인 실패', errorMsg);
        }

        emit('login-fail', { message: errorMsg });
    } finally {
        loadingStore.hideLoading();
    }
};

async function getUserInfo(socialPlatform) {
    try {
        loadingStore.showLoading();
        const url = `/api/customer/oauth/login`;
        const config = { method: 'post' };
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                const userData = response.data.data;
                if (!userData.is_registered) {
                    console.log('Social user not registered in our system:', userData);
                    emit('social-needs-signup', {
                        site_type: userData.site_type,
                        key: userData.key, // Or necessary identifier
                        name: userData.name,
                        mobile: userData.mobile,
                        email: userData.email,
                        birth_date: userData.birth_date,
                        access_token: accessToken.value // Pass token if needed for signup
                    });
                    closeModal();
                } else {
                    handleSocialLogin(userData.access_token, userData.refresh_token, userData.email, userData.name, userData.mobile, userData.birth_date);
                }
            },
            onError(err) {
                const errorMsg = err.response?.data?.msg || err.message || '소셜 정보 확인 중 오류 발생';
                console.error('Error fetching user info:', errorMsg);
                if (toastComponentRef.value) {
                    toastComponentRef.value.showToast('error', '오류', errorMsg);
                }
                emit('login-fail', { message: errorMsg }); // Signal failure
            }
        });

        setHeader('Content-Type', 'application/json');
        execute({
            access_token: `Bearer ${accessToken.value}`,
            site_type: socialPlatform
        });
    } catch (err) {
        console.error('Error in getUserInfo setup:', err.message);
        emit('login-fail', { message: '소셜 로그인 처리 중 오류 발생' });
    } finally {
        loadingStore.hideLoading(); // Ensure loading hides even on setup error
    }
}

const handleSocialLoginSuccess = (data) => {
    accessToken.value = data.token;
    platform.value = data.platform;
    getUserInfo(data.platform);
};

const handleSocialLoginFail = (error) => {
    console.error('Social SDK login failed:', error);
    if (toastComponentRef.value) {
        toastComponentRef.value.showToast('error', '소셜 로그인 실패', error.message || '로그인 중 오류가 발생했습니다.');
    }
    emit('login-fail', { message: error.message || '소셜 로그인 연동 실패' });
    loadingStore.hideLoading();
};

// --- UI Actions ---
const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;
};

const validateLoginId = () => {
    loginid.value = loginid.value; // Keep original behavior if intended
};

const handleRequestSignUp = () => {
    emit('request-signup');
    closeModal(); // Close login popup before parent opens signup
};

const handleRequestPasswordReset = () => {
    emit('request-password-reset');
    closeModal();
};

const handleFindPassword = () => {
    showModal.value = true;
    resetLoginId.value = '';
    resetName.value = '';
    resetPhone.value = '';
};

const closeFindPassword = () => {
    showModal.value = false;
    resetLoginId.value = '';
    resetName.value = '';
    resetPhone.value = '';
};

const handlePasswordReset = () => {
    loadingStore.showLoading();
    try {
        const url = `/api/customer/login/password`;
        const config = { method: 'post' };
        const { execute, setHeader } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                if (response.data.success && toastComponentRef.value) {
                    toastComponentRef.value.showToast('success', '비밀번호 재설정', '입력된 연락처로 임시비밀번호를 전송하였습니다.');
                }
                showModal.value = false;
            },
            onError(err) {
                const errorMsg = err.response?.data?.msg || err.message || '비밀번호 찾기 중 중 오류 발생';
                console.error('Error fetching user info:', errorMsg);
                if (toastComponentRef.value) {
                    toastComponentRef.value.showToast('error', '오류', errorMsg);
                }
            }
        });

        setHeader('Content-Type', 'application/json');
        execute({
            is_email: resetIsEmail.value,
            name: resetName.value,
            login_id: resetLoginId.value,
            phone: resetIsEmail.value ? '' : resetPhone.value
        });
    } catch (err) {
        console.error('Error resetPassword:', err.message);
    } finally {
        loadingStore.hideLoading();
    }
};

const closeModal = () => {
    isVisible.value = false; // Triggers watcher to emit update:modelValue
};

watch(resetIsEmail, (isEmail) => {
    if (isEmail) resetPhone.value = '';
});

function formatPhone() {
    // 숫자만 추출, 최대 11자리
    const digits = resetPhone.value.replace(/\D/g, '').slice(0, 11);
    if (digits.length > 7) {
        resetPhone.value = `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    } else if (digits.length > 3) {
        resetPhone.value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
        resetPhone.value = digits;
    }
}
</script>

<template>
    <div v-if="isVisible" class="login-popup-overlay">
        <!--@click.self="closeModal"-->
        <div class="login-popup-container" @click.stop>
            <ToastComponent ref="toastComponentRef" />
            <button @click="closeModal" class="login-popup-close-btn" aria-label="Close login popup">&times;</button>

            <div class="logo-container">
                <img src="/image/gomduri_icon/h_logo.png" alt="Logo" class="logo-image" />
            </div>

            <div class="login-popup-content">
                <form @submit.prevent="handleLogin()" class="space-y-4 md:space-y-4">
                    <div>
                        <label for="popup-loginid" class="input-label">아이디 </label>
                        <input v-model="loginid" @input="validateLoginId" type="text" name="popup-loginid" id="popup-loginid" autocomplete="username" class="custom-input" placeholder="아이디를 입력하세요." required />
                    </div>
                    <div class="password-container">
                        <label for="popup-password" class="input-label">패스워드</label>
                        <input
                            v-model="password"
                            :type="showPassword ? 'text' : 'password'"
                            name="popup-password"
                            id="popup-password"
                            autocomplete="current-password"
                            placeholder="패스워드를 입력하세요."
                            class="custom-input password-input"
                            required
                        />
                        <button type="button" class="password-toggle" @click="togglePasswordVisibility">
                            <i :class="['pi', showPassword ? 'pi-eye-slash' : 'pi-eye']"></i>
                        </button>
                    </div>
                    <div v-if="isCapsLockOn" class="text-red-500 text-sm mt-1">CapsLock이 켜져 있습니다.</div>
                    <div class="flex items-center justify-between">
                        <div class="flex items-start">
                            <div class="flex items-center h-5">
                                <input
                                    v-model="rememberMeCustomer"
                                    id="popup-remember"
                                    type="checkbox"
                                    class="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800 cursor-pointer"
                                />
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="popup-remember" class="text-gray-500 dark:text-gray-300 cursor-pointer">아이디 저장</label>
                            </div>
                        </div>
                        <div class="flex items-center">
                            <span class="text-sm text-gray-400 hover:underline dark:text-primary-500 cursor-pointer mr-3" @click="handleRequestSignUp">회원가입</span>
                            <span class="text-sm text-gray-400 hover:underline dark:text-primary-500 cursor-pointer" @click="handleFindPassword">비밀번호 재설정</span>
                        </div>
                    </div>
                    <button type="submit" class="custom-button">로그인</button>

                    <div class="social-login">
                        <div class="social-buttons">
                            <NaverLoginButton @login-success="handleSocialLoginSuccess" @login-fail="handleSocialLoginFail" />
                            <KakaoLoginButton @login-success="handleSocialLoginSuccess" @login-fail="handleSocialLoginFail" />
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-[1001]">
        <div class="relative p-4 w-full max-w-md max-h-full">
            <div class="relative bg-white rounded-lg shadow dark:bg-gray-700">
                <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
                    <h3 class="text-xl font-semibold text-gray-900 dark:text-white">비밀번호 재설정</h3>
                    <button @click="closeFindPassword" class="login-popup-close-btn" aria-label="Close">&times;</button>
                </div>
                <div class="p-4 md:p-5">
                    <form class="space-y-4" @submit.prevent="handlePasswordReset">
                        <div>
                            <span class="input-label">전송 방식</span>
                            <label> <input type="radio" v-model="resetIsEmail" :value="true" /> 이메일 </label>
                            <label class="ml-4"> <input type="radio" v-model="resetIsEmail" :value="false" /> 휴대폰 </label>
                        </div>

                        <div>
                            <label for="reset-loginid" class="input-label">아이디(이메일)</label>
                            <input v-model="resetLoginId" type="email" id="reset-loginid" class="custom-input" placeholder="example@domain.com" required />
                        </div>

                        <div>
                            <label for="reset-name" class="input-label">이름</label>
                            <input v-model="resetName" type="text" id="reset-name" class="custom-input" placeholder="이름을 입력하세요." required />
                        </div>

                        <div v-if="!resetIsEmail" :key="showModal">
                            <label for="reset-phone" class="input-label">연락처</label>
                            <input v-model="resetPhone" @input="formatPhone" type="tel" id="reset-phone" class="custom-input" placeholder="010-1234-5678" title="010-1234-5678 형식으로 입력하세요." required />
                        </div>

                        <button type="submit" class="custom-button">임시비밀번호 전송</button>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* --- Base Modal Styles --- */
.login-popup-overlay {
    position: fixed;
    inset: 0; /* top, right, bottom, left = 0 */
    background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent background */
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000; /* Ensure it's above other content */
    padding: 1rem; /* Padding for smaller screens */
    animation: fadeIn 0.3s ease-out;
}

.login-popup-container {
    background-color: #fff; /* White background for the modal */
    border-radius: 0.5rem; /* Rounded corners */
    box-shadow:
        0 4px 6px -1px rgb(0 0 0 / 0.1),
        0 2px 4px -2px rgb(0 0 0 / 0.1);
    padding: 2rem; /* Internal padding (adjust sm:p-10 if needed) */
    padding-top: 3rem; /* Extra top padding to accommodate close button */
    width: 100%;
    max-width: 28rem; /* max-w-md */
    position: relative; /* For positioning the close button */
    animation: slideIn 0.3s ease-out;
    max-height: 90vh; /* Limit height and allow scrolling if needed */
    overflow-y: auto; /* Add scroll for tall content */
}

.login-popup-close-btn {
    position: absolute;
    top: 0.85rem;
    right: 0.85rem;
    background: none;
    border: none;
    font-size: 1.1rem;
    line-height: 1;
    color: #6b7280; /* gray-500 */
    cursor: pointer;
    padding: 0.25rem;
}
.login-popup-close-btn:hover {
    color: #1f2937; /* gray-800 */
}

.login-popup-content {
    /* Styles for the form area */
    margin-top: 1rem; /* Space below logo */
}

/* --- Animations (Keep if desired) --- */
@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(-20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

/* --- Copied & Adapted Styles (Keep relevant ones) --- */
.logo-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    /* margin-top: 1rem; */ /* Adjusted padding */
    cursor: pointer;
    object-fit: contain;
    margin-bottom: 1rem; /* Reduced bottom margin */
}

.logo-image {
    height: 4.5rem;
    object-fit: contain;
    margin-bottom: 1.4rem;
}

.custom-button {
    width: 100%;
    color: white;
    background-color: #0038ea; /* Blue */
    font-family: 'SCoreDream400', sans-serif; /* Add fallback */
    border-radius: 0.3rem;
    font-size: 0.93rem;
    padding: 0.625rem 1.25rem;
    text-align: center;
    transition: background-color 0.2s;
    border: none; /* Ensure no default border */
    cursor: pointer;
}

.custom-button:hover {
    background-color: #00229a; /* Darker Blue */
}

.custom-button:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(0, 56, 234, 0.4); /* Focus ring */
}

.custom-input {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid #e2e8f0; /* gray-300 */
    border-radius: 0.375rem; /* rounded-md */
    background-color: #f8fafc; /* gray-50 */
    color: #1a202c; /* gray-800 */
    font-family: 'SCoreDream300', sans-serif;
    font-size: 0.9rem;
    line-height: 1.25rem;
}

.custom-input:focus {
    outline: none;
    border-color: #0038ea; /* Blue */
    box-shadow: 0 0 0 1px #0038ea; /* Focus ring */
}

.custom-input::placeholder {
    color: #a0aec0; /* gray-400 */
}

.input-label {
    display: block;
    margin-bottom: 0.5rem;
    margin-left: 0.1rem;
    font-size: 0.875rem;
    font-family: 'SCoreDream400', sans-serif;
    color: #1f2937; /* gray-800 */
}

.password-container {
    position: relative;
}

.password-input {
    padding-right: 2.5rem; /* Space for the eye icon */
}

.password-toggle {
    position: absolute;
    right: 0.8rem;
    top: 50%; /* Center vertically relative to the label+input height */
    transform: translateY(-50%); /* Adjust vertical centering */
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2rem;
    color: #6b7280; /* gray-500 */
    padding: 0.25rem; /* Add some clickable area */
    margin-top: 0.9rem; /* Adjust alignment if label causes shift */
}
/* Ensure PrimeIcons are loaded */
.pi {
    font-family: 'primeicons';
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    line-height: 1;
    display: inline-block;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.social-login {
    margin-top: 1.5rem; /* Add space before social buttons */
    width: 100%;
    padding-top: 1rem; /* Add padding above social buttons */
    border-top: 1px solid #e2e8f0; /* Separator line */
}

.social-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.75rem; /* Increased gap */
    /* margin-top: 0.5rem; */ /* Removed as padding-top added to parent */
    width: 100%;
}

.text-red-500 {
    color: #ef4444;
}
.text-sm {
    font-size: 0.875rem;
}
.mt-1 {
    margin-top: 0.25rem;
}
.flex {
    display: flex;
}
.items-center {
    align-items: center;
}
.justify-between {
    justify-content: space-between;
}
.items-start {
    align-items: flex-start;
}
.h-5 {
    height: 1.25rem;
}
.w-4 {
    width: 1rem;
}
.h-4 {
    height: 1rem;
}
/* Add other Tailwind utility classes if needed */
.ml-3 {
    margin-left: 0.75rem;
}
.mr-3 {
    margin-right: 0.75rem;
}
.text-gray-500 {
    color: #6b7280;
}
.text-gray-400 {
    color: #9ca3af;
}
.hover\:underline:hover {
    text-decoration: underline;
}
.cursor-pointer {
    cursor: pointer;
}
.rounded-lg {
    border-radius: 0.5rem;
}
.border {
    border-width: 1px;
}
.border-gray-300 {
    border-color: #d1d5db;
}
.rounded {
    border-radius: 0.25rem;
}
.bg-gray-50 {
    background-color: #f9fafb;
}

/* Responsive adjustments */
@media (max-width: 600px) {
    .login-popup-container {
        padding: 1.5rem;
        padding-top: 3rem; /* Ensure space for close button */
    }
}
</style>
